import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { Copy, Trash2, Volume2, Waves } from 'lucide-react';
import { usePieceDispatch, usePieceSelector } from '../../model/piece-context';
import type { AudioLayer } from '../../model/types';
import { VolumePopover } from './volume-popover';

/**
 * Duración fija del fade out cuando el user lo activa manualmente. La rampa la
 * aplica el runtime (`audio-sync.ts`) leyendo `content.fadeOutMs`.
 */
const MANUAL_FADE_OUT_MS = 3000;

type Props = {
  onDuplicate: () => void;
  onDelete: () => void;
  layerId?: string;
  children: React.ReactElement;
};

/**
 * Right-click menu compartido por la columna de layers y las filas del timeline.
 *
 * Cuando `layerId` apunta a un audio de música, el menú incorpora:
 *   - "Volumen…" → abre un Popover con slider anclado al children.
 *   - "Aplicar / Quitar fade out final" → toggle sobre `content.fadeOutMs`.
 *
 * El popover state vive acá para que los consumers no tengan que preocuparse.
 */
export const LayerContextMenu: React.FC<Props> = ({ onDuplicate, onDelete, layerId, children }) => {
  const dispatch = usePieceDispatch();
  const [volumeOpen, setVolumeOpen] = useState(false);

  // Solo re-renderea si cambia el layer específico (evita re-renders por cambios en otros layers).
  const audioLayer = usePieceSelector(m => {
    if (!layerId) return null;
    const layer = m.layers[layerId];
    if (!layer || layer.kind !== 'audio') return null;
    return layer as AudioLayer;
  });

  const isMusic = audioLayer !== null;
  const gain = audioLayer?.content.gain ?? 1;
  const hasFadeOut = audioLayer?.content.fadeOutMs != null && audioLayer.content.fadeOutMs > 0;

  const handleGainChange = (next: number) => {
    if (!layerId) return;
    dispatch({ type: 'patchAudioContent', id: layerId, patch: { gain: next } });
  };

  const toggleFadeOut = () => {
    if (!layerId) return;
    dispatch({
      type: 'patchAudioContent',
      id: layerId,
      patch: { fadeOutMs: hasFadeOut ? undefined : MANUAL_FADE_OUT_MS }
    });
  };

  const items = [
    { key: 'duplicate', label: 'Duplicar', icon: <Copy size={14} /> },
    ...(isMusic
      ? [
          { type: 'divider' as const },
          { key: 'volume', label: 'Volumen…', icon: <Volume2 size={14} /> },
          {
            key: 'fadeout',
            label: hasFadeOut ? 'Quitar fade out final' : 'Aplicar fade out final',
            icon: <Waves size={14} />
          }
        ]
      : []),
    { type: 'divider' as const },
    { key: 'delete', label: 'Eliminar', icon: <Trash2 size={14} />, danger: true }
  ];

  const dropdown = (
    <Dropdown
      trigger={['contextMenu']}
      menu={{
        items,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === 'duplicate') onDuplicate();
          else if (key === 'delete') onDelete();
          else if (key === 'volume') setVolumeOpen(true);
          else if (key === 'fadeout') toggleFadeOut();
        }
      }}
    >
      {children}
    </Dropdown>
  );

  if (!isMusic) return dropdown;

  return (
    <VolumePopover open={volumeOpen} onOpenChange={setVolumeOpen} gain={gain} onGainChange={handleGainChange}>
      {dropdown}
    </VolumePopover>
  );
};
